// /requests mobile card — grouped layout (header, route, metrics, metadata).
// Custom layout rather than the generic stacked DataTable fallback so the
// metrics row stays on a single line and the metadata block can express SLA
// state, manager identity, and update timing as distinct sections.
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RequestStatus } from '@magimp/shared';
import { Avatar, Icon } from '../../../ui/index.js';
import { NotificationDot } from '../../notifications/index.js';
import { formatRequestDensity } from '../density.js';
import { formatRequestDestination } from '../destination-display.js';
import { formatRelative, formatSLA, formatVolume, formatWeight } from '../format.js';
import { formatRequestOrigin } from '../origin-display.js';
import type { RequestView } from '../types.js';
import { CarrierResponsesBar } from './CarrierResponsesBar.js';
import { RequestListThumbnail } from './RequestListThumbnail.js';
import { RequestStatusPill } from './RequestStatusPill.js';
import { UnreadBadge } from './UnreadBadge.js';
import { RequestSelectionCheckbox } from '../selection/RequestSelectionCheckbox.js';

interface RequestMobileCardProps {
  r: RequestView;
  now: number;
  selected: boolean;
  selectionMode?: boolean;
  selectionChecked?: boolean;
  onSelect: () => void;
  onToggleSelection?: (shiftKey: boolean) => void;
  unread: { unreadCount: number; isMarkedUnread: boolean };
  notificationCount?: number;
}

/**
 * @aiPurpose Mobile (`<900px`) card for the requests list. Replaces the desktop
 *   grid row with a vertically stacked layout that keeps every column readable
 *   in a 320px viewport.
 * @aiInvariant The article carries onClick + Enter/Space activation. Lint
 *   treats article as non-interactive, so a block disable for
 *   `jsx-a11y/no-noninteractive-element-interactions` and
 *   `jsx-a11y/no-noninteractive-tabindex` brackets the element.
 */
export function RequestMobileCard({
  r,
  now,
  selected,
  selectionMode = false,
  selectionChecked = false,
  onSelect,
  onToggleSelection = () => undefined,
  unread,
  notificationCount,
}: RequestMobileCardProps) {
  const { t } = useTranslation('requests');
  const sla = formatSLA(
    {
      status: r.status,
      slaDeadline: r.slaDeadline,
      inWorkAt: r.inWorkAt,
      closedAt: r.closedAt,
      processedAt: r.processedAt,
    },
    now,
  );
  const archived = r.status === RequestStatus.ARCHIVE;
  const thumbnailItem = r.items.find((item) => item.photoKeys.length > 0);
  return (
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
    <article
      aria-label={r.name}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-1)',
        cursor: 'pointer',
        opacity: archived ? 0.75 : 1,
        boxShadow: selected ? 'inset 3px 0 0 var(--accent-1)' : 'none',
        outline: 'none',
      }}
      onFocus={(e) => {
        e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-ring)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = selected ? 'inset 3px 0 0 var(--accent-1)' : 'none';
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: 'var(--fg-3)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        {selectionMode && (
          <RequestSelectionCheckbox
            checked={selectionChecked}
            label={t('export.bulk.selectRequest', { id: r.id })}
            onToggle={onToggleSelection}
          />
        )}
        <span>{r.id}</span>
        <span style={{ flex: 1 }} />
        <RequestStatusPill status={r.status} />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          fontSize: 14,
          fontWeight: 600,
          color: 'var(--fg-1)',
        }}
      >
        {thumbnailItem?.photoKeys[0] ? (
          <RequestListThumbnail
            requestId={r.id}
            itemId={thumbnailItem.id}
            objectKey={thumbnailItem.photoKeys[0]}
          />
        ) : null}
        {/* WHY (#570): trailing UnreadBadge slot inside the name row,
            mirrors desktop. `min-width: 0` + `flex: 1 1 0` lets the name
            wrap/truncate while keeping the badge fully visible on narrow
            viewports. */}
        <span
          style={{
            flex: '1 1 0',
            minWidth: 0,
            wordBreak: 'break-word',
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <span>{r.name}</span>
          {r.kind === 'CONTAINER' && (
            <span
              data-testid="request-container-badge"
              style={{
                fontSize: 10,
                lineHeight: 1,
                fontWeight: 500,
                padding: '2px 6px',
                borderRadius: 'var(--r-1)',
                background: 'var(--bg-3)',
                color: 'var(--fg-2)',
                border: '1px solid var(--border-1)',
              }}
            >
              {t('list.row.containerBadge')}
            </span>
          )}
          {r.kind === 'SIMPLE' && (
            <span
              data-testid="request-simple-badge"
              style={{
                fontSize: 10,
                lineHeight: 1,
                fontWeight: 500,
                padding: '2px 6px',
                borderRadius: 'var(--r-1)',
                background: 'var(--bg-3)',
                color: 'var(--fg-2)',
                border: '1px solid var(--border-1)',
              }}
            >
              {t('list.row.simpleBadge')}
            </span>
          )}
        </span>
        <UnreadBadge unreadCount={unread.unreadCount} isMarkedUnread={unread.isMarkedUnread} />
        {notificationCount !== undefined && notificationCount > 0 ? (
          <NotificationDot count={notificationCount} />
        ) : null}
      </div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--fg-3)',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          flexWrap: 'wrap',
        }}
      >
        <Icon name="arrow-right" size={11} />
        <span>
          {formatRequestOrigin(r, t('draftDefaults.from'))} →{' '}
          {formatRequestDestination(r, { short: true })}
        </span>
        {r.items.length > 0 && (
          <span>
            · {r.items.length} {t('common.items')}
          </span>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          paddingTop: 4,
          borderTop: '1px solid var(--border-2)',
        }}
      >
        <MobileFact label={t('list.columns.answers')}>
          <CarrierResponsesBar carriers={r.carriers} />
        </MobileFact>
        <MobileFact label={t('list.columns.weight')}>
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              color: r.weight == null ? 'var(--fg-4)' : 'var(--fg-1)',
              fontSize: 12,
            }}
          >
            {formatWeight(r.weight)}
          </span>
        </MobileFact>
        <MobileFact label={t('list.columns.volume')}>
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              color: r.volume == null ? 'var(--fg-4)' : 'var(--fg-1)',
              fontSize: 12,
            }}
          >
            {formatVolume(r.volume)}
          </span>
        </MobileFact>
        <MobileFact label={t('list.columns.density')}>
          <span
            data-testid="request-mobile-density"
            style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--fg-1)', fontSize: 12 }}
          >
            {formatRequestDensity(r.weight, r.volume)}
          </span>
        </MobileFact>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          paddingTop: 4,
          borderTop: '1px solid var(--border-2)',
          fontSize: 12,
          color: 'var(--fg-2)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 8,
          }}
        >
          <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>{t('list.columns.deadlines')}</span>
          {sla.level === 'none' ? (
            <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>—</span>
          ) : (
            <span
              data-testid="sla-primary"
              data-sla-level={sla.level}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 12,
                fontVariantNumeric: 'tabular-nums',
                fontWeight: sla.level === 'ok' ? 400 : 500,
                color:
                  sla.level === 'err'
                    ? 'var(--status-error-fg)'
                    : sla.level === 'warn'
                      ? 'var(--status-warn-fg)'
                      : sla.level === 'success'
                        ? 'var(--status-success-fg)'
                        : 'var(--fg-2)',
              }}
            >
              <Icon name={sla.level === 'err' ? 'timer-off' : 'timer'} size={12} />
              {sla.text}
            </span>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 8,
            fontSize: 11,
            color: 'var(--fg-4)',
          }}
        >
          <span>&nbsp;</span>
          <span>{formatRelative(r.createdAt, now)}</span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>{t('list.columns.manager')}</span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              minWidth: 0,
              color: 'var(--fg-1)',
            }}
          >
            <Avatar name={r.manager.name} size={18} />
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: 12,
              }}
            >
              {r.manager.name}
            </span>
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 8,
          }}
        >
          <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>{t('list.columns.updated')}</span>
          <span
            data-testid="request-mobile-updated-at"
            style={{
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--fg-3)',
              fontSize: 12,
            }}
          >
            {formatRelative(r.updatedAt)}
          </span>
        </div>
      </div>
    </article>
    /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
  );
}

function MobileFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase' }}>
        {label}
      </span>
      {children}
    </div>
  );
}
